import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle, Car, User, Phone, Mail, MapPin, DollarSign, Calendar, Settings, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HelpMeFind = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferredMake: '',
    preferredModel: '',
    alternativeMake: '',
    alternativeModel: '',
    minBudget: '',
    maxBudget: '',
    preferredYear: '',
    transmission: '',
    fuelType: '',
    province: '',
    mustHaveFeatures: [],
    additionalNotes: '',
    contactPreference: 'whatsapp',
    urgency: 'browsing'
  });

  useEffect(() => {
    document.title = 'Help Me Find a Car | AutoSearch SA';
  }, []);

  const makes = ['Toyota', 'Volkswagen', 'Ford', 'BMW', 'Mercedes-Benz', 'Audi', 'Hyundai', 'Kia', 'Nissan', 'Mazda', 'Honda', 'Suzuki', 'Isuzu', 'Haval', 'GWM', 'Chery', 'BYD', 'Renault', 'Peugeot', 'Jeep', 'Land Rover', 'Volvo', 'Other'];
  const provinces = ['Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape', 'Free State', 'Mpumalanga', 'Limpopo', 'North West', 'Northern Cape'];
  const features = ['Sunroof', 'Leather Seats', 'Apple CarPlay/Android Auto', 'Reverse Camera', 'Parking Sensors', 'Cruise Control', 'Heated Seats', 'Navigation', 'Bluetooth', '4x4/AWD', 'Towbar', 'Low Mileage'];

  const handleFeatureToggle = (feature) => {
    setForm(prev => ({
      ...prev,
      mustHaveFeatures: prev.mustHaveFeatures.includes(feature)
        ? prev.mustHaveFeatures.filter(f => f !== feature)
        : [...prev.mustHaveFeatures, feature]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        lead_type: 'help_me_find',
        contact_preference: form.contactPreference,
        message: `Help Me Find Request:
        
Primary Choice: ${form.preferredMake} ${form.preferredModel}
Alternative: ${form.alternativeMake} ${form.alternativeModel}
Budget: R${form.minBudget || '0'} - R${form.maxBudget || 'Any'}
Year: ${form.preferredYear || 'Any'}
Transmission: ${form.transmission || 'Any'}
Fuel: ${form.fuelType || 'Any'}
Province: ${form.province || 'Any'}
Must-Have Features: ${form.mustHaveFeatures.join(', ') || 'None specified'}
Urgency: ${form.urgency}
Notes: ${form.additionalNotes}`
      });

      setSubmitted(true);
    } catch (e) {
      console.error('Submission failed:', e);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md text-center shadow-lg">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">We're On It!</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Our team will search our network of dealers to find your perfect car. Expect to hear from us within 24 hours.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-12">
        <div className="container">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">Help Me Find a Car</h1>
                <p className="text-blue-200">Let us do the searching for you</p>
              </div>
            </div>
            <p className="text-lg text-blue-100 mt-4">
              Tell us what you're looking for and our team will search across our network of trusted dealers to find your perfect match.
            </p>
          </div>
        </div>
      </section>

      <div className="container py-10">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          {/* Contact Details */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Your Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  placeholder="082 123 4567"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preferred Contact Method</label>
              <div className="flex gap-2 flex-wrap">
                {['whatsapp', 'call', 'email'].map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => setForm({ ...form, contactPreference: method })}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                      form.contactPreference === method
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-blue-300'
                    }`}
                  >
                    {method === 'whatsapp' && '💬 WhatsApp'}
                    {method === 'call' && '📞 Call'}
                    {method === 'email' && '📧 Email'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Car Preferences */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-600" />
              What Are You Looking For?
            </h2>
            
            <div className="space-y-6">
              {/* Primary Choice */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">First Choice</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    value={form.preferredMake}
                    onChange={(e) => setForm({ ...form, preferredMake: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Make</option>
                    {makes.map(make => <option key={make} value={make}>{make}</option>)}
                  </select>
                  <input
                    type="text"
                    value={form.preferredModel}
                    onChange={(e) => setForm({ ...form, preferredModel: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="Model (e.g., Hilux, Polo)"
                  />
                </div>
              </div>

              {/* Alternative Choice */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Alternative (Optional)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    value={form.alternativeMake}
                    onChange={(e) => setForm({ ...form, alternativeMake: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select Make</option>
                    {makes.map(make => <option key={make} value={make}>{make}</option>)}
                  </select>
                  <input
                    type="text"
                    value={form.alternativeModel}
                    onChange={(e) => setForm({ ...form, alternativeModel: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                    placeholder="Model"
                  />
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Budget Range
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="number"
                      value={form.minBudget}
                      onChange={(e) => setForm({ ...form, minBudget: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                      placeholder="Min Budget"
                    />
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                    <input
                      type="number"
                      value={form.maxBudget}
                      onChange={(e) => setForm({ ...form, maxBudget: e.target.value })}
                      className="w-full pl-8 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                      placeholder="Max Budget"
                    />
                  </div>
                </div>
              </div>

              {/* Other Specs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Year From</label>
                  <select
                    value={form.preferredYear}
                    onChange={(e) => setForm({ ...form, preferredYear: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Any Year</option>
                    {[2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015].map(year => (
                      <option key={year} value={year}>{year}+</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transmission</label>
                  <select
                    value={form.transmission}
                    onChange={(e) => setForm({ ...form, transmission: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Any</option>
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fuel Type</label>
                  <select
                    value={form.fuelType}
                    onChange={(e) => setForm({ ...form, fuelType: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Any</option>
                    <option value="petrol">Petrol</option>
                    <option value="diesel">Diesel</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="electric">Electric</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Province</label>
                  <select
                    value={form.province}
                    onChange={(e) => setForm({ ...form, province: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Any Province</option>
                    {provinces.map(prov => <option key={prov} value={prov}>{prov}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Must-Have Features */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" />
              Must-Have Features
            </h2>
            <div className="flex flex-wrap gap-2">
              {features.map((feature) => (
                <button
                  key={feature}
                  type="button"
                  onClick={() => handleFeatureToggle(feature)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                    form.mustHaveFeatures.includes(feature)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {form.mustHaveFeatures.includes(feature) && '✓ '}{feature}
                </button>
              ))}
            </div>
          </div>

          {/* Urgency & Notes */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">How Soon Do You Need a Car?</label>
                <select
                  value={form.urgency}
                  onChange={(e) => setForm({ ...form, urgency: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white"
                >
                  <option value="browsing">Just Browsing</option>
                  <option value="1month">Within 1 Month</option>
                  <option value="2weeks">Within 2 Weeks</option>
                  <option value="asap">ASAP - Ready to Buy</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Additional Notes</label>
                <textarea
                  value={form.additionalNotes}
                  onChange={(e) => setForm({ ...form, additionalNotes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-700 dark:text-white resize-none"
                  placeholder="Any specific requirements or preferences..."
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition disabled:opacity-50"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </>
            ) : (
              <>
                Submit Request
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-4">
            We'll search our dealer network and get back to you within 24 hours with matching vehicles.
          </p>
        </form>
      </div>
    </div>
  );
};

export default HelpMeFind;
